import { useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import ExpandableSection from "@/components/job/ExpandableSection";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { inputStep } from "@/constants/inputs";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";

const toSafeString = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value);
};

export function MdmtRevision() {
  const { sections, setChecks } = useCUMechanicalJobStore(
    useShallow((state) => ({
      sections: state.sections,
      setChecks: state.setChecks,
    }))
  );

  const checks = sections.checks;
  const [isExpanded, setIsExpanded] = useState(true);
  const [mdmt, setMdmt] = useState("");
  const [revision, setRevision] = useState("");

  const needsMdmt = checks.data.do_you_want_to_add_mdmt_to_package === true;
  const needsRevision = checks.data.is_this_revision === true;
  const shouldShow = checks.isDataFilled && (needsMdmt || needsRevision);

  useEffect(() => {
    if (!shouldShow) {
      setMdmt("");
      setRevision("");
      return;
    }
    setMdmt(toSafeString(checks.data.mdmt));
    setRevision(toSafeString(checks.data.revision));
  }, [checks.data.mdmt, checks.data.revision, shouldShow]);

  const revisionValue = Number.parseFloat(revision);
  const revisionValid =
    !needsRevision || (revision.trim().length > 0 && Number.isFinite(revisionValue) && revisionValue > 0);
  const mdmtValid = !needsMdmt || mdmt.trim().length > 0;
  const canSave = mdmtValid && revisionValid;

  const isComplete =
    (!needsMdmt || (checks.data.mdmt !== null && checks.data.mdmt !== undefined)) &&
    (!needsRevision ||
      (checks.data.revision !== null && checks.data.revision !== undefined && checks.data.revision > 0));

  const status = !shouldShow ? "hidden" : isComplete ? "done" : "open";

  const handleSave = () => {
    setChecks({
      data: {
        ...checks.data,
        mdmt: needsMdmt && mdmt.trim().length > 0 ? Number.parseFloat(mdmt) : null,
        revision: needsRevision && revision.trim().length > 0 ? Number.parseFloat(revision) : null,
      },
      status: "done",
    });
    setIsExpanded(false);
  };

  return (
    <ExpandableSection
      isExpanded={isExpanded}
      setIsExpanded={() => setIsExpanded((prev) => !prev)}
      status={status}
      title="MDMT and Revision"
    >
      <div className="flex flex-col gap-3">
        {needsMdmt && (
          <TextInput
            label="MDMT"
            type="number"
            step={inputStep}
            placeholder="Enter MDMT"
            value={mdmt}
            endAdornment={
              <Typography variant="text-small" weight="regular" className="text-gray-400">
                °F
              </Typography>
            }
            onChange={(e) => {
              setMdmt(toSafeString(e.currentTarget.value));
            }}
          />
        )}

        {needsRevision && (
          <TextInput
            label="Revision"
            type="number"
            step={inputStep}
            placeholder="Enter revision"
            value={revision}
            error={
              revision.trim().length > 0 && Number.parseFloat(revision) <= 0
                ? "Revision must be greater than 0"
                : undefined
            }
            onChange={(e) => {
              setRevision(toSafeString(e.currentTarget.value));
            }}
          />
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <Button onClick={handleSave} disabled={!canSave}>
          Save
        </Button>
      </div>
    </ExpandableSection>
  );
}
